import { StringParam, useQueryParams } from "use-query-params";
import type { Task } from "@common/types/task";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { PageCoverLoader } from "@design-system/PageCoverLoader/PageCoverLoader";
import { SelectedTaskContainer } from "@components/tasks/SelectedTaskContainer";
import { TasksList } from "@components/tasks/TasksList";
import { TasksProvider } from "@components/tasks/TasksProvider";
import { TasksPageLayout } from "@components/tasks/TasksPageLayout";
import { TaskViewFallback } from "@components/tasks/TaskViewFallback";
import { useTasksContext } from "@components/tasks/tasks.context";
import { useCachedTaskListFilters } from "@components/tasks/useCachedTaskListFilters";
import { useOrganizationTasksListFiltersCache } from "@hooks/view/useTasksListFiltersCache";
import { ORGANIZATION_TASK_LIST_CAPABILITIES } from "@components/tasks/taskListCapabilities";
import { ORGANIZATION_TASK_LIST_CACHE_KEYS } from "@components/tasks/taskListCacheKeys";
import {
    TaskClientIconColorsContext,
    TaskClientNamesContext,
} from "@components/tasks/taskClientNamesContext";
import { useNavigationTaskIds } from "@components/tasks/useNavigationTaskIds";
import { useScope } from "@state/scopeContext.lib";
import { SubScopeProvider } from "@state/subScopeContext";
import { useOrganizationTaskWorkspaceData } from "./useOrganizationTaskWorkspaceData";
import { useOrganizationTasks } from "./useOrganizationTasks";
import { useSelectedTaskWorkspaceId } from "./useSelectedTaskWorkspaceId";
import { TasksLanding } from "@components/tasks/tasksLanding";
import { useOrganizationTasksPageLanding } from "./useOrganizationTasksPageLanding";
import { useReportFocusedTasksClient } from "./focusedTasksClient";

const NO_TASKS: Task[] = [];

const OrganizationTasksContent = ({
    landing: { landingViewId, defaultQuery, initialOverlayFilters },
}: {
    landing: TasksLanding;
}) => {
    const { selectedTaskId, closeTask } = useTasksContext();
    const [{ taskWorkspaceId: taskWorkspaceIdQueryParam }] = useQueryParams({
        taskWorkspaceId: StringParam,
    });

    const filtersCache = useOrganizationTasksListFiltersCache();
    const { tasksQuery, setFilters, overlayFilters, setOverlayFilters } =
        useCachedTaskListFilters({
            cache: filtersCache,
            defaultQuery,
            initialOverlayFilters,
        });
    const { navigationTaskIds, handleNavigationTaskIdsChange } =
        useNavigationTaskIds();
    const focusedClientIds = tasksQuery.clientIds ?? [];
    useReportFocusedTasksClient(
        focusedClientIds.length === 1 && !selectedTaskId
            ? focusedClientIds[0]
            : null,
    );

    const {
        tasks,
        clientNamesById,
        clientIconColorsById,
        isPlaceholderData,
        hasLoadError,
        retryFailed,
    } = useOrganizationTasks(tasksQuery);
    const loadedTasks = tasks ?? NO_TASKS;
    const workspaceData = useOrganizationTaskWorkspaceData(loadedTasks);
    const listedTaskWorkspaceId = useSelectedTaskWorkspaceId({
        tasks: loadedTasks,
        selectedTaskId,
    });
    const linkedTaskWorkspaceId =
        taskWorkspaceIdQueryParam &&
        clientNamesById.has(taskWorkspaceIdQueryParam)
            ? taskWorkspaceIdQueryParam
            : undefined;
    const selectedTaskWorkspaceId =
        listedTaskWorkspaceId ?? linkedTaskWorkspaceId;

    const loadErrorNotice = hasLoadError ? (
        <EmptyState
            header="Failed to load tasks"
            ctaText="Retry"
            action={retryFailed}
            buttonVariant="secondary"
            emphasizeHeader={false}
        />
    ) : undefined;

    return (
        <TaskClientNamesContext.Provider value={clientNamesById}>
            <TaskClientIconColorsContext.Provider value={clientIconColorsById}>
                <TasksPageLayout
                    list={
                        <TasksList
                            filters={tasksQuery}
                            setFilters={setFilters}
                            tasks={tasks}
                            isShowingPreviousTasks={isPlaceholderData}
                            hasTaskLoadError={hasLoadError}
                            onNavigationTaskIdsChange={
                                handleNavigationTaskIdsChange
                            }
                            workspaceData={workspaceData}
                            capabilities={ORGANIZATION_TASK_LIST_CAPABILITIES}
                            cacheKeys={ORGANIZATION_TASK_LIST_CACHE_KEYS}
                            landingViewId={landingViewId}
                            tableNotice={loadErrorNotice}
                            overlayFilters={overlayFilters}
                            onOverlayFiltersChange={(payload) =>
                                setOverlayFilters((prev) => ({
                                    ...prev,
                                    ...payload,
                                }))
                            }
                        />
                    }
                    taskView={
                        selectedTaskWorkspaceId ? (
                            <SubScopeProvider
                                value={{ workspaceId: selectedTaskWorkspaceId }}
                            >
                                <SelectedTaskContainer
                                    tasks={loadedTasks}
                                    navigationTaskIds={navigationTaskIds}
                                />
                            </SubScopeProvider>
                        ) : (
                            <TaskViewFallback
                                isLoading={tasks === null || isPlaceholderData}
                                close={closeTask}
                            />
                        )
                    }
                />
            </TaskClientIconColorsContext.Provider>
        </TaskClientNamesContext.Provider>
    );
};

const OrganizationTasksLandingGate = () => {
    const landing = useOrganizationTasksPageLanding();

    if (!landing) {
        return <PageCoverLoader />;
    }

    return <OrganizationTasksContent landing={landing} />;
};

export const OrganizationTasksPage = () => {
    const { organizationId } = useScope();

    return (
        <TasksProvider key={organizationId}>
            <OrganizationTasksLandingGate />
        </TasksProvider>
    );
};
